'use client';

/**
 * Keeps a page at its Figma size on screens wider than the design width.
 *
 * WHY
 *   The site's root font-size is `calc(800vw / 1920)` up to 1920px (1rem = 8px
 *   at 1920, so Figma px = rem × 8), but above 1920 it switches to
 *   `calc(800vw / 2200)`: the root drops to 6.99px at 1921 and every rem-based
 *   size shrinks 12.5% at once. It only reaches 8px again at 2200, then keeps
 *   growing.
 *
 * WHAT IT DOES
 *   Renders one CSS rule while the page is mounted:
 *     @media (min-width: 1921px) { :root:root { font-size: 8px } }
 *   `:root:root` outranks the global `:root` rules by specificity (no
 *   !important). It unmounts with the page, so no other page is affected.
 *
 * MODES
 *   freeze (default)  root pinned at 8px above the design width — everything
 *                     keeps its exact Figma size, extra width becomes margin.
 *   proportional      root stays `calc(800vw / designWidth)` — the page keeps
 *                     scaling with the viewport, proportions as in the comp.
 *   Both equal 8px at exactly the design width, so there is no jump.
 *
 * CAVEATS
 *   - Use it only inside page components, never in a shared layout: the rule
 *     is global while mounted (header and footer included, on purpose).
 *   - On a wide monitor, navigating to a page without it changes the scale.
 *
 * USAGE
 *   <DesignScaleLock />                        // freeze above 1920
 *   <DesignScaleLock mode="proportional" />    // keep scaling past 1920
 *   <DesignScaleLock designWidth={1440} />     // comp drawn at 1440
 *   <DesignScaleLock from={2200} />            // start the lock at another width
 */
export default function DesignScaleLock({
  designWidth = 1920,
  mode = 'freeze',
  from,
}: {
  designWidth?: number;
  mode?: 'freeze' | 'proportional';
  from?: number;
}) {
  const width = Number(designWidth) || 1920;
  const start = Number(from) || width + 1;
  const fontSize = mode === 'freeze' ? '8px' : `calc(800vw / ${width})`;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `@media (min-width:${start}px){:root:root{font-size:${fontSize}}}`,
      }}
    />
  );
}
